import {Person, PersonAddress} from './person.data'
import {HuntingEvent} from './event.data'
import * as moment from 'moment'
import { diffObject, firestoreTimestampToDate, isDate, isObject, updatesToFirestoreUpdates } from '../utils/obejct-diff'
import { IDependencyMapping } from './db-dependencies'
import { NetworkConstants } from './network.constants'

export const EventInvitationStatusesList = [
    "assigned",
    "approved",
    "pending",
    "pending_viewed",
    "cancled",
    "accepted",
    "declined",
    "expired"
]

export enum EventInvitationStatus {
    ASSIGNED = "assigned",  // EventInvite created not further processed
    APPROVED = "approved",  // soll eingeladen werden.
    PENDING = "pending",    // Invitation send no response
    PENDING_VIEWED = "pending_viewed",
    CANCLED = "cancled",    // Invitation send / accepted / declined but cancled
    ACCEPTED = "accepted",  
    DECLINED = "declined", 
    EXPIRED = "expired"     // invitation expired 
}

export const EventInvitationChartColorMapping = new Map([
    [`${EventInvitationStatus.ASSIGNED}`, 'rgba(0,0,0,.1)'],
    [`${EventInvitationStatus.DECLINED}`, '#f97762'],
    [`${EventInvitationStatus.ACCEPTED}`, '#b8e886'],
    [`${EventInvitationStatus.PENDING}`, '#fbb465'],
    [`${EventInvitationStatus.PENDING_VIEWED}`, '#fbd365'],
    [`${EventInvitationStatus.EXPIRED}`, '#c277a6'],
    [`${EventInvitationStatus.APPROVED}`, '#9eddcc'],
    [`${EventInvitationStatus.CANCLED}`, 'rgba(0,0,0,.2)'],
])

export enum NoteFlag {
    IMPORTANT = "important", 
    INFO = "info",
    NONE = "none"
}

export class Notes {
    
    public flag: NoteFlag = NoteFlag.NONE

    constructor(
        public timeStamp: Date,
        public text: string,
        flag?: NoteFlag 
    ) {
        if (flag) this.flag = flag
    }
}

export class EventInvitation {

    identifier: string|undefined = undefined
    contextId: string|undefined = undefined
    // event specific data
    eventId: string|undefined
    eventName: string|undefined
    eventDate: Date|undefined
    huntingDistrictId: string|undefined
    huntingDistrictName: string|undefined
    eventUpdateTimestamp: Date|undefined = undefined
    // person specific data
    personId: string|undefined
    personInvitationRef: string|undefined
    personAnrede: string|undefined = undefined
    personTitelAkademisch: string|undefined = undefined
    personTitelAndere: string|undefined = undefined
    personVorname: string|undefined = undefined
    personNachname: string|undefined = undefined
    personAnzeigeName: string|undefined = undefined
    personGeschlecht: string|undefined = undefined
    personTelefon: string|undefined = undefined
    personEmailAddresse: string|undefined = undefined
    personLegalPersonalAddress: PersonAddress|undefined = undefined
    personBillingAddress: PersonAddress|undefined = undefined
    personUpdateTimestamp: Date|undefined

    creationDate: Date|undefined = undefined
    updateTimestamp: Date|undefined = undefined
    // invitation data
    processingStatus: EventInvitationStatus|undefined
    invitationApproved: Date|undefined
    invitationExpiration: Date|undefined
    invitationSend: Date|undefined
    invitationViewed: Date|undefined
    invitationAccepted: Date|undefined
    invitationDeclined: Date|undefined
    notes: Notes[]|undefined

    siteReferralURL: string|undefined // only set from function when (get)

    personDeleted: Date|undefined


    constructor() {}

    searchFilter(filter: string): boolean {
        const str = `${this.personAnzeigeName}${this.personVorname}${this.personNachname}${this.personEmailAddresse}${this.huntingDistrictName}${this.eventName}`.toLowerCase()
        return str.includes(filter)
    }

    setProcessingStatus(status: EventInvitationStatus) {
        const now = new Date()
        const prevStatus = this.processingStatus
        switch(status) {
            case(EventInvitationStatus.PENDING): {
                this.invitationSend = now
                break
            }
            case(EventInvitationStatus.APPROVED): {
                this.invitationApproved = now
                break
            }
            case(EventInvitationStatus.ACCEPTED): {
                this.invitationAccepted = now
                break   
            }
            case(EventInvitationStatus.CANCLED): {
                
                break   
            }
            case(EventInvitationStatus.DECLINED): {
                this.invitationDeclined = now
                break   
            }
            case(EventInvitationStatus.EXPIRED): {
                
                break   
            }
        }
        this.processingStatus = status
    }
    
    /**
     * Function for skipping objects and data while deepcopying class.
     * used as optional Input for deepCopy(any, replacerFn)
     * @param key 
     */
     replacerFn(key: string) {
        // if (key == "context" || key == "outlines") return false
        // else 
        return true
    }

    static builder = {
        
        newEventIvitation(event: HuntingEvent, person:Person): EventInvitation|never {
            if (!EventInvitation.valid(event, person)) throw new Error(`Not enough data...`)
            const invite = new EventInvitation()
            invite.contextId = event.contextId
            const {
                date,
                name, 
                huntingDistrictId,
                huntingDistrictName
            } = event
            invite.eventId = event.identifier
            invite.eventName = name
            invite.eventDate = date
            invite.eventUpdateTimestamp = event.updateTimestamp
            invite.huntingDistrictId = huntingDistrictId
            invite.huntingDistrictName = huntingDistrictName
            invite.processingStatus = EventInvitationStatus.ASSIGNED

            const {
                emailAddresse,
                vorname,
                nachname,
                telefon,
                anzeigeName,
                geschlecht,
                legalPersonalAddress,
                billingAddress,
                anrede,
                titelAkademisch,
                titelAndere,
                identifier,
                invitationRef
            } = person
            invite.personId = identifier
            invite.personAnrede = anrede
            invite.personAnzeigeName = anzeigeName
            invite.personBillingAddress = billingAddress
            invite.personEmailAddresse = emailAddresse
            invite.personVorname = vorname
            invite.personNachname = nachname
            invite.personUpdateTimestamp = person.updateTimestamp
            invite.personGeschlecht = geschlecht
            invite.personTelefon = telefon
            invite.personLegalPersonalAddress = legalPersonalAddress
            invite.personTitelAkademisch = titelAkademisch
            invite.personTitelAndere = titelAndere
            invite.personInvitationRef = invitationRef
            return invite
        },
        fromAny(element: any): EventInvitation {
            const {
                creationDate, updateTimestamp,
                invitationSend, invitationViewed,
                invitationExpiration, eventDate,
                invitationAccepted, invitationDeclined,
                eventUpdateTimestamp, personUpdateTimestamp,
                invitationApproved, personDeleted, siteReferralURL
            } = element
            const invite = new EventInvitation()
            Object.assign(invite, element)
            invite.creationDate = creationDate ? isDate(creationDate) ? creationDate : firestoreTimestampToDate(creationDate) : invite.creationDate
            invite.updateTimestamp = updateTimestamp ? isDate(updateTimestamp) ? updateTimestamp : firestoreTimestampToDate(updateTimestamp) : invite.updateTimestamp
            invite.invitationSend = invitationSend ? isDate(invitationSend) ? invitationSend : firestoreTimestampToDate(invitationSend) : invite.invitationSend
            invite.invitationViewed = invitationViewed ? isDate(invitationViewed) ? invitationViewed : firestoreTimestampToDate(invitationViewed) : invite.invitationViewed
            invite.invitationExpiration = invitationExpiration ? isDate(invitationExpiration) ? invitationExpiration : firestoreTimestampToDate(invitationExpiration) : invite.invitationExpiration
            invite.invitationAccepted = invitationAccepted ? isDate(invitationAccepted) ? invitationAccepted : firestoreTimestampToDate(invitationAccepted) : invite.invitationAccepted
            invite.invitationDeclined = invitationDeclined ? isDate(invitationDeclined) ? invitationDeclined : firestoreTimestampToDate(invitationDeclined) : invite.invitationDeclined
            invite.eventUpdateTimestamp = eventUpdateTimestamp ? isDate(eventUpdateTimestamp) ? eventUpdateTimestamp : firestoreTimestampToDate(eventUpdateTimestamp) : invite.eventUpdateTimestamp
            invite.personUpdateTimestamp = personUpdateTimestamp ? isDate(personUpdateTimestamp) ? personUpdateTimestamp : firestoreTimestampToDate(personUpdateTimestamp) : invite.personUpdateTimestamp
            invite.eventDate = eventDate ? isDate(eventDate) ? eventDate : firestoreTimestampToDate(eventDate) : invite.eventDate
            invite.invitationApproved = invitationApproved ? isDate(invitationApproved) ? invitationApproved : firestoreTimestampToDate(invitationApproved) : invite.invitationApproved
            invite.personDeleted = personDeleted ? isDate(personDeleted) ? personDeleted : firestoreTimestampToDate(personDeleted) : invite.personDeleted
            invite.siteReferralURL = siteReferralURL ? siteReferralURL : invite.siteReferralURL
            return invite
        }
    }

    static valid(event: HuntingEvent, person: Person) {
        const { date, name, huntingDistrictId,
            huntingDistrictName
        } = event
        const {
            vorname,
            nachname,
            anzeigeName,
            invitationRef
        } = person
        return name 
            // && huntingDistrictId 
            //&& huntingDistrictName 
            && moment().isBefore(moment(date)) 
            // && ((vorname && nachname) || anzeigeName))
            && invitationRef
    }
}

export class EventTransaction {
    identifier: string|undefined
    eventInvitationId: string|undefined
    contextId: string|undefined
    creationDate: Date|undefined
    // documented if changed by human...
    userDisplayName: string|undefined
    userId: string|undefined
    deviceName: string|undefined
    // transaction list
    transactions: Transaction[]|SingleTransaction|undefined
    updateOperations: any
    
    constructor() {}

    /**
     * Function for skipping objects and data while deepcopying class.
     * used as optional Input for deepCopy(any, replacerFn)
     * @param key 
     */
     replacerFn(key: string) {
        if (key == "updateOperations") return false
        else return true
    }
    
    static builder = {
        createUpdate(
                oldEventInvitation: any, 
                newEventInvitation: any, 
                additionalInformation?: {userId: string|undefined, userDisplayName: string|undefined, deviceName: string|undefined}
            ): EventTransaction|never {
            const { contextId, eventId } = newEventInvitation
            if (!contextId || !eventId) throw new Error(`Invalid data, no context or eventId`)
            const updates = diffObject(oldEventInvitation, newEventInvitation, {})
            let transactions: Transaction[] = []
            for (let [key, val] of Object.entries(updates)) {
                let value = val as any
                if (isObject(value)) {
                    const operationType = (value['oldValue'] && value['newValue']) ? 'update' 
                        : (!value['oldValue'] && value['newValue']) ? 'create' 
                        : (value['oldValue'] && !value['newValue']) ? 'delete' : undefined
                    if (!operationType) throw new Error(`Invalid operation Type!`)
                    transactions.push(
                        {
                            fieldName: key,
                            oldValue: value['oldValue'],
                            newValue: value['newValue'],
                            operation: operationType
                        }
                    )
                }
            }
            const eventTransaction = new EventTransaction()
            eventTransaction.eventInvitationId = oldEventInvitation.identifier
            eventTransaction.contextId = contextId
            eventTransaction.transactions = transactions
            eventTransaction.updateOperations = updatesToFirestoreUpdates(updates)
            eventTransaction.creationDate = new Date()
            if (additionalInformation) Object.assign(eventTransaction, additionalInformation)
            return eventTransaction
        },
        createFromNewInvitation(
            invitation: EventInvitation,
            additionalInformation?: {userId: string|undefined, userDisplayName: string|undefined, deviceName: string|undefined}
        ): EventTransaction {
            const eventTransaction = new EventTransaction()

            Object.assign(eventTransaction, {
                contextId: invitation.contextId,
                userDisplayName: additionalInformation?.userDisplayName,
                userId: additionalInformation?.userId,
                deviceName: additionalInformation?.deviceName,
                eventInvitationId: invitation.identifier,
                creationDate: new Date(),
                transactions: [
                    {
                        operation: 'create',
                        referenceId: invitation.identifier
                    }
                ]
            })

            return eventTransaction
        },
        setFromAny(element: any): EventTransaction {
            const {creationDate} = element
            const eventTransaction = new EventTransaction()
            Object.assign(eventTransaction, element)
            // dates... 
            eventTransaction.creationDate = creationDate ? isDate(creationDate) ? creationDate : firestoreTimestampToDate(creationDate) : eventTransaction.creationDate
            return eventTransaction
        }
    }
}

export const DependencyEventInvitationToPerson: IDependencyMapping = {
    collectionName: NetworkConstants.COLLECTION_PERSON,
    mapping: new Map<string, string>([
        ["personEmailAddresse", "emailAddresse"],
        ["personNachname", "nachname"],
        ["personVorname", "vorname"],
        ["personAnzeigeName", "anzeigeName"],
        ["personGeschlecht", "geschlecht"],
        ["personAnrede", "anrede"],
        ["personTitelAkademisch", "titelAkademisch"],
        ["personTitelAndere", "titelAndere"],
        ["personTelefon", "telefon"],
        ["personLegalPersonalAddress.zip", "legalPersonalAddress.zip"],
        ["personLegalPersonalAddress.address1", "legalPersonalAddress.address1"],
        ["personLegalPersonalAddress.address2", "legalPersonalAddress.address2",],
        ["personLegalPersonalAddress.city", "legalPersonalAddress.city"],
        ["personLegalPersonalAddress.county", "legalPersonalAddress.county"],
        ["personLegalPersonalAddress.country", "legalPersonalAddress.country"],
        ["personBillingAddress.zip", "billingAddress.zip"],
        ["personBillingAddress.address1", "billingAddress.address1"],
        ["personBillingAddress.address2", "billingAddress.address2"],
        ["personBillingAddress.city", "billingAddress.city"],
        ["personBillingAddress.county", "billingAddress.county"],
        ["personBillingAddress.country", "billingAddress.country"],
    ]),
    mappingIdentifier: "personId",
    getDoc: (firestore: any, identifier: string): any => {
        return firestore.collection(NetworkConstants.COLLECTION_PERSON)
            .doc(identifier)
    },
    getQuery: (firestore: any, origin: any, now: Date|null) => {
        return null
    }
}

export const DependencyEventInvitationToEvent: IDependencyMapping = {
    collectionName: NetworkConstants.COLLECTION_EVENT,
    mapping: new Map<string, string>([
        ["eventName", "name"],
        ["eventDate", "date"],
        ["huntingDistrictId", "huntingDistrictId"],
        ["huntingDistrictName", "huntingDistrictName"]
    ]),
    mappingIdentifier: "eventId",
    getDoc: (firestore: any, identifier: string): any => {
        return firestore.collection(NetworkConstants.COLLECTION_EVENT)
            .doc(identifier)
    },
    getQuery: (firestore: any, origin: any, now: Date|null) => {
        return null
    }
}

export const DependencyEventInvitationToHuntingDistrict: IDependencyMapping = {
    collectionName: NetworkConstants.COLLECTION_HUNTING_DISTRICT,
    mapping: new Map<string, string>([
        ["huntingDistrictName", "name"]
    ]),
    mappingIdentifier: "huntingDistrictId",
    getDoc: (firestore: any, identifier: string): any => {
        return firestore.collection(NetworkConstants.COLLECTION_HUNTING_DISTRICT)
            .doc(identifier)
    },
    getQuery: (firestore: any, origin: any, now: Date|null) => {
        return null
    }
}

interface Transaction {
    fieldName: string
    operation: string
    newValue: any
    oldValue: any
}

interface SingleTransaction {
    operation: string,
    referenceId: string
}